/**
 * Status — everything this build is, in one place.
 *
 * TABBED, WITH THE ENVIRONMENT PINNED (Ben, 2026-08-29). The verdict banner
 * and the six environment facts answer the question this page exists for, so
 * they sit above the tabs and are never a click away. Everything else is
 * reference material that was only reachable by scrolling past all of it.
 *
 * The selected tab lives in the URL (?tab=…), so a link to the promotion path
 * or the unguarded-route list is a link someone can paste into Slack. An
 * unknown or missing value falls back to the first tab rather than rendering
 * nothing.
 *
 * Where Priori staff land, and where the Resiliency & API content now lives
 * (Ben, 2026-08-28): two screens describing the same build meant two places to
 * look and two chances to be out of date.
 *
 * PROVENANCE IS LABELLED ON EVERY SECTION, because most of this is not live.
 * The architecture, the checks and the recorded route count were fixed when
 * the image was BUILT; the health figures and the route sweep are read NOW.
 * A page that mixes the two without saying so invites reading a stale fact as
 * a current one.
 */
import { useEffect, useRef, useState, type ReactNode } from 'react'
import { useSearchParams } from 'react-router-dom'

import { navInventory } from '../components/shell/nav'
import DatabaseInfo from './DatabaseInfo'
import { ApiError, api, type Session } from '../lib/api'
import { BackupControls } from './BackupControls'
import './status.css'
import './resiliency.css'

// Pre-rendered mermaid diagrams (SVG strings via Vite ?raw import).
// Source .mmd files live beside these; re-render with:
//   node scripts/render-mermaid-ink.mjs frontend/src/pages/diagrams/*.mmd
import architectureSvg from './diagrams/architecture.svg?raw'
import deployFlowSvg from './diagrams/deploy-flow.svg?raw'
import dataFlowSvg from './diagrams/data-flow.svg?raw'

const DIAGRAMS = [
  { id: 'architecture', title: 'Platform architecture', svg: architectureSvg },
  { id: 'deploy-flow', title: 'Deploy flow', svg: deployFlowSvg },
  { id: 'data-flow', title: 'Data pipeline', svg: dataFlowSvg },
] as const

type Health = {
  status: string
  db: boolean
  r2: boolean
  r2_latency_ms: number | null
  r2_bucket: string | null
  env: string
  revision: string
  release: string
  schema_era: number
  alembic_head: string | null
  alembic_db_rev: string | null
}

/** /api/backups. `state` is graded server-side so the page cannot disagree
 *  with the smoke test about what "stale" means. */
type Backups = {
  state: 'ok' | 'stale' | 'missing' | 'unknown'
  stale_after_hours: number
  bucket?: string
  object?: string
  taken_at?: string
  age_hours?: number
  size_bytes?: number
  reason?: string
}

type Node = { id: string; name: string; kind: string; tech?: string; detail?: string }
type Layer = { id: string; title: string; nodes: Node[] }
type Edge = { from: string; to: string }
type Check = { kind: string; id: string; title: string; file: string; needle: string; ok: boolean }
/** Which commit a test run describes, and whether the tree was clean.
 *  Without it a coverage number is quoted later about code that has moved. */
type ProvenanceFacts = {
  commit: string
  dirty: boolean
  generated_at: string
  suites_failed: boolean
}
type CoverageFile = { path: string; statements: number; covered: number; percent: number }
type CoverageReport = {
  suite: string
  measure: string
  percent: number | null
  files: number
  below_floor: number
  floor: number
  worst: CoverageFile[]
}

type Doc = {
  generated_at: string | null
  environment: string
  commit: { sha: string | null; short: string | null; subject: string | null; branch: string | null; committed_at: string | null }
  architecture: { layers: Layer[]; edges: Edge[] }
  api: { available: boolean; reason?: string; census?: Census; endpoints: unknown[] }
  tests: {
    available: boolean
    reason?: string
    provenance: ProvenanceFacts | null
    suites?: {
      suite: string
      group_count: number
      totals: { tests: number; failures: number; errors: number; skipped: number; seconds: number }
      groups: { name: string; count: number; failures: number; errors: number; skipped: number }[]
    }[]
    manual_checks: { check: string; why: string; who: string }[]
  }
  coverage: {
    available: boolean
    reason?: string
    provenance: ProvenanceFacts | null
    reports?: CoverageReport[]
  }
  security: {
    controls: { id: string; title: string; detail: string; verify: string[] }[]
    accepted_risks: { id: string; title: string; impact: string; ledgered: string }[]
    smoke_assertions: string[]
  }
  checks: Check[]
  promotion: {
    summary: string
    stages: { env: string; host: string; how: string }[]
    rollback: { how: string; caveat: string }
  }
  // Optional: an image built before 2026-08-30 has no infrastructure block,
  // and the tab is skipped rather than rendering an empty section.
  infrastructure?: {
    summary: string
    stack: { path: string; owns: string }[]
    not_owned: string[]
    applied_by: string
    paths_verified: boolean
  }
}
type Census = { total: number; public: number; org_scoped: number }

type BuildDoc = {
  visible: boolean
  available: boolean
  environment: string
  revision: string
  release: string
  reason: string | null
  document: Doc | null
  live_api: {
    census: { total: number; public: number; org_scoped: number }
    public_routes: { rule: string; methods: string[]; module: string }[]
  }
}

/** One number with a label. `alarm` tints it; the same treatment the API
 *  census uses for a non-zero public-route count. */
function Stat({ n, label, alarm }: { n: number; label: string; alarm?: boolean }) {
  return (
    <div className={`rz-stat${alarm ? ' is-alarm' : ''}`}>
      <div className="t-metric">{n}</div>
      <div className="t-label">{label}</div>
    </div>
  )
}

/**
 * Which commit a test run describes.
 *
 * Printed rather than assumed, because the results are produced before the
 * image is built and could in principle describe something else. `dirty` says
 * the tree had uncommitted changes when the suites ran — the numbers are then
 * real, but they are not a property of any commit anybody can check out.
 */
function Provenance({ of }: { of: ProvenanceFacts | null }) {
  if (!of) {
    return (
      <p className="muted rz-note">
        These results carry no provenance record, so which commit they describe
        is unknown. Treat them as indicative, not as a fact about this build.
      </p>
    )
  }
  return (
    <p className="muted rz-note">
      Ran against <code>{of.commit.slice(0, 12)}</code> at {day(of.generated_at)}
      {of.dirty && (
        <> — <strong>with uncommitted changes in the tree</strong>, so these
        numbers do not describe any commit you can check out</>
      )}.
    </p>
  )
}


type State =
  | { status: 'loading' }
  | { status: 'ready'; health: Health; build: BuildDoc | null; backups: Backups | null }
  | { status: 'error'; message: string }

const day = (iso: string | null) => (iso ? iso.replace('T', ' ').slice(0, 19) : '—')

/** Says where a section's numbers came from. The whole point of the page. */
function Source({ when, live }: { when?: string | null; live?: boolean }) {
  return (
    <span className="st-source t-label">
      {live ? 'read now' : `recorded at build${when ? ` · ${day(when)}` : ''}`}
    </span>
  )
}

/**
 * One fact.
 *
 * `ok` is TRISTATE and the distinction is the point: true is green, false is
 * red, and undefined is neither — a value like the schema era is not a health
 * signal and colouring it green would teach the reader that green means
 * nothing in particular. Only the things that can actually be wrong get a
 * colour, so a red one is worth looking at.
 */
function Row({ label, value, ok, children }: {
  label: string; value?: string; ok?: boolean; children?: ReactNode
}) {
  const state = ok === undefined ? '' : ok ? ' is-ok' : ' is-bad'
  return (
    <div className={`st-row${state}`}>
      <span className="st-row__label t-label">{label}</span>
      <span className={`st-row__value${state}`}>{children ?? value}</span>
    </div>
  )
}

/* The diagram is CSS grid, not a drawing library: four labelled columns of
   boxes with the edge list rendered beneath. A dependency that has to be
   installed, versioned and kept alive to draw eleven arrows is not worth it,
   and a diagram nobody can read the source of is the thing this page exists
   to avoid. */
function Architecture({ layers, edges }: { layers: Layer[]; edges: Edge[] }) {
  const name = (id: string) =>
    layers.flatMap((l) => l.nodes).find((n) => n.id === id)?.name ?? id
  return (
    <>
      <div className="rz-arch">
        {layers.map((layer) => (
          <div className="rz-arch__col" key={layer.id}>
            <div className="rz-arch__title t-label">{layer.title}</div>
            {layer.nodes.map((n) => (
              <div className={`rz-node rz-node--${n.kind}`} key={n.id}>
                <div className="rz-node__name">{n.name}</div>
                {n.tech && <div className="rz-node__tech t-data">{n.tech}</div>}
                {n.detail && <div className="rz-node__detail">{n.detail}</div>}
              </div>
            ))}
          </div>
        ))}
      </div>
      <div className="rz-edges">
        <div className="t-label rz-edges__title">Flows</div>
        <ul>
          {edges.map((e) => (
            <li key={`${e.from}->${e.to}`}>
              <span className="rz-edge__from">{name(e.from)}</span>
              <span className="rz-edge__arrow">→</span>
              <span className="rz-edge__to">{name(e.to)}</span>
            </li>
          ))}
        </ul>
      </div>
    </>
  )
}

/** `body` is a FUNCTION, not a node. JSX children are evaluated where they
 *  are written, so an array of seven ready-made bodies builds all seven on
 *  every render — and a field missing from any one of them throws the whole
 *  page, including the six tabs that did not need it. Only the open tab's
 *  body is called. */
type Tab = { id: string; label: string; alarm?: boolean; body: () => ReactNode }

/**
 * The tab strip, bound to ?tab= in the URL.
 *
 * Keyboard behavior is the WAI-ARIA tabs pattern: arrows move between tabs,
 * Home/End jump to the ends. Written out rather than pulled in, because the
 * whole control is thirty lines and a dependency for it would be a new
 * package in a frozen-ish tree.
 */
function Tabs({ tabs }: { tabs: Tab[] }) {
  const [params, setParams] = useSearchParams()
  const refs = useRef<(HTMLButtonElement | null)[]>([])

  const requested = params.get('tab')
  // An unknown ?tab= renders the first tab rather than an empty page. The URL
  // is deliberately NOT corrected: rewriting someone's link on arrival hides
  // the typo that sent them here.
  const activeIndex = Math.max(0, tabs.findIndex((t) => t.id === requested))
  const active = tabs[activeIndex]!

  const select = (index: number) => {
    const next = new URLSearchParams(params)
    next.set('tab', tabs[index]!.id)
    // Pushed, not replaced: Back returns to the tab you came from, which is
    // what "the URL updates" is supposed to mean.
    setParams(next)
    refs.current[index]?.focus()
  }

  const onKeyDown = (e: React.KeyboardEvent) => {
    const last = tabs.length - 1
    const to =
      e.key === 'ArrowRight' ? (activeIndex === last ? 0 : activeIndex + 1)
      : e.key === 'ArrowLeft' ? (activeIndex === 0 ? last : activeIndex - 1)
      : e.key === 'Home' ? 0
      : e.key === 'End' ? last
      : null
    if (to === null) return
    e.preventDefault()
    select(to)
  }

  return (
    <>
      <div className="st-tabs" role="tablist" aria-label="Status sections">
        {tabs.map((tab, i) => (
          <button
            key={tab.id}
            ref={(el) => { refs.current[i] = el }}
            role="tab"
            id={`st-tab-${tab.id}`}
            aria-controls={`st-panel-${tab.id}`}
            aria-selected={i === activeIndex}
            // Only the selected tab is in the tab order; arrows move within
            // the strip. A tablist that takes N tab stops makes a keyboard
            // user walk every section to reach the page below it.
            tabIndex={i === activeIndex ? 0 : -1}
            className="st-tab"
            // The dot is decorative, so the warning has to reach a screen
            // reader some other way. An aria-label carries it; a visually
            // hidden span would have needed an .sr-only utility this codebase
            // does not have — and without it the text simply renders.
            aria-label={tab.alarm ? `${tab.label} — needs attention` : undefined}
            onClick={() => select(i)}
            onKeyDown={onKeyDown}
          >
            {tab.label}
            {tab.alarm && <span className="st-tab__alarm" aria-hidden />}
          </button>
        ))}
      </div>
      <div role="tabpanel"
           id={`st-panel-${active.id}`}
           aria-labelledby={`st-tab-${active.id}`}>
        {active.body()}
      </div>
    </>
  )
}


export default function Status({ session }: { session: Session }) {
  const [state, setState] = useState<State>({ status: 'loading' })

  useEffect(() => {
    Promise.all([
      api<Health>('/health'),
      // The build document is admin-only; a staff member without it still gets
      // the page, minus the sections the document feeds.
      api<BuildDoc>('/api/v1/build-document').catch(() => null),
      // Same treatment as the build document: admin-only, and its absence
      // costs one section rather than the page.
      api<Backups>('/api/v1/backups').catch(() => null),
    ])
      .then(([health, build, backups]) => setState({ status: 'ready', health, build, backups }))
      .catch((e: unknown) => setState({
        status: 'error',
        message: e instanceof ApiError ? e.message : String(e),
      }))
  }, [])

  if (state.status === 'loading') return <p className="muted">Checking…</p>
  if (state.status === 'error') {
    return <div className="panel error">Could not reach the API — {state.message}</div>
  }

  const { health, build, backups } = state
  const doc = build?.document ?? null
  const migrationsCurrent = health.alembic_db_rev === health.alembic_head

  /* The verdict, computed from every signal that can actually be wrong. Add a
     signal here and the banner covers it — the alternative is a banner that
     says NORMAL while a new red row sits underneath it. */
  const problems: string[] = []
  if (health.status !== 'ok') problems.push(`status is "${health.status}"`)
  if (!health.db) problems.push('database unreachable')
  if (!health.r2) problems.push('R2 storage unreachable')
  if (!migrationsCurrent) problems.push('database is not at this image’s migration')
  if (backups && (backups.state === 'stale' || backups.state === 'missing')) {
    problems.push(backups.state === 'missing'
      ? 'no database export has ever landed'
      : `database export is ${backups.age_hours}h old`)
  }
  const allWell = problems.length === 0

  /* github.com/priorisports/webapp is this repo; the page is staff-only, so a
     link into a private repo is fine — it resolves for anyone who can already
     read this screen. Only 40-hex revisions are linked. */
  const isSha = /^[0-9a-f]{40}$/.test(health.revision)
  const shortSha = isSha ? health.revision.slice(0, 12) : health.revision
  const commitUrl = isSha
    ? `https://github.com/priorisports/webapp/commit/${health.revision}`
    : null

  /* "Built", not "deployed", and the difference is not pedantry: this is the
     build document's own timestamp, stamped when the image was assembled. The
     deploy follows within minutes in this pipeline, but they are not the same
     event and only one of them is recorded in the image.

     SHOWN IN THE READER'S OWN TIMEZONE, named. The stored value is UTC
     ("2026-08-29T15:09:53Z") and this used to slice the string at 16
     characters, which dropped the Z and printed UTC as though it were local —
     a clock that is wrong by your offset and says nothing about it. Wearing
     no label was the problem; wearing the wrong one silently is worse. The
     exact UTC value stays in the title for anyone correlating with logs. */
  const builtAt = doc?.generated_at ? new Date(doc.generated_at) : null
  const builtLabel = builtAt && !Number.isNaN(builtAt.getTime())
    // Explicit components, NOT dateStyle/timeStyle: those cannot be combined
    // with timeZoneName and toLocaleString throws a TypeError on the pair,
    // which would take the whole page down rather than misformat one row.
    ? builtAt.toLocaleString(undefined, {
        year: 'numeric', month: 'short', day: 'numeric',
        hour: 'numeric', minute: '2-digit', timeZoneName: 'short',
      })
    : null
  const nav = navInventory(session)

  return (
    <>
      <h1 className="page-title">Status</h1>
      <p className="page-sub">
        What this build is, what proved it, and how it ships. Signed in as{' '}
        {session.user.email}
        {session.user.is_platform_admin ? ' · platform admin' : ' · Priori staff'}
        {session.org ? ` · viewing ${session.org.name}` : ' · no club selected'}
      </p>

      <section className="st-section">
        <h2 className="st-h2">This environment <Source live /></h2>

        {/* One verdict, across the width, before any detail. The page used to
            open with seven equal boxes and leave the reader to work out which
            of them was the alarm — "ok" in the same grey as the schema era.
            Everything that can be wrong is folded in here, so a green banner
            means all of it is fine and a red one names what is not. */}
        <div className={`st-verdict${allWell ? ' is-ok' : ' is-bad'}`}>
          <span className="st-verdict__light" aria-hidden />
          <span className="st-verdict__word">
            {allWell ? 'NORMAL OPERATIONS' : 'NEEDS ATTENTION'}
          </span>
          <span className="st-verdict__detail">
            {allWell
              ? 'Serving, database reachable, R2 reachable, migrations current.'
              : problems.join(' · ')}
          </span>
        </div>

        <div className="st-grid">
          <Row label="environment" value={health.env} />
          <Row label="database" value={health.db ? 'reachable' : 'unreachable'} ok={health.db} />
          <Row label="R2 storage"
               value={health.r2
                 ? `reachable${health.r2_latency_ms != null ? ` (${health.r2_latency_ms}ms)` : ''}`
                 : health.r2_bucket ? 'unreachable' : 'not configured'}
               ok={health.r2} />
          <Row label="migrations"
               value={migrationsCurrent
                 ? `current (${health.alembic_db_rev ?? '—'})`
                 : `BEHIND — db ${health.alembic_db_rev ?? 'none'}, image ${health.alembic_head ?? 'none'}`}
               ok={migrationsCurrent} />
          <Row label="release">
            <span title={doc?.generated_at ?? undefined}>
              {health.release}
              {builtLabel && <> · built {builtLabel}</>}
            </span>
          </Row>
          <Row label="revision">
            {/* Linked only when it looks like a real commit. Local builds
                report "local", and a link to github.com/…/commit/local is a
                404 dressed as a fact. */}
            {commitUrl
              ? <a href={commitUrl} target="_blank" rel="noreferrer">{shortSha}</a>
              : health.revision}
          </Row>
          <Row label="schema era" value={String(health.schema_era)} />
        </div>
        {!migrationsCurrent && (
          <div className="panel st-alarm">
            The database is not at the migration this image ships. The deploy
            runs migrations before traffic moves, so this means something ran
            out of band — or an image was deployed against a database that had
            already moved past it.
          </div>
        )}
      </section>

      {/* Everything below the environment card is reference material. It was
          reachable only by scrolling past all of it, so it is tabbed — and the
          tab is in the URL, which makes a link to the promotion path or the
          unguarded-route list something you can paste to somebody.

          Tabs are built from what this build actually published: an image with
          no build document offers one "Build" tab carrying the reason, rather
          than five empty ones. */}
      <Tabs tabs={[
        // Database sits with Backups: both answer "what shape is the data in",
        // and it was a rail entry of its own until 2026-08-30. Unconditional —
        // it depends on a nightly snapshot, not on the build document the
        // tabs below are built from.
        {
          id: 'database',
          label: 'Database',
          body: () => <DatabaseInfo />,
        },
        {
          id: 'diagrams',
          label: 'Diagrams',
          body: () => (
            <>
              {DIAGRAMS.map((d) => (
                <section className="st-section" key={d.id}>
                  <h2 className="st-h2">{d.title}</h2>
                  <div className="st-diagram"
                       dangerouslySetInnerHTML={{ __html: d.svg }} />
                </section>
              ))}
            </>
          ),
        },
        ...(backups || session.user.is_platform_admin ? [{
          id: 'backups',
          label: 'Backups',
          // The dot is the reason the tab strip can be trusted while it hides
          // things: a section reporting a problem says so from the strip,
          // without being opened.
          alarm: backups?.state === 'stale' || backups?.state === 'missing',
          body: () => (
            <>

                    {backups && (
                      <section className="st-section">
                        <h2 className="st-h2">Backups <Source live /></h2>
                        <div className="st-grid">
                          <Row label="nightly export"
                               value={
                                 backups.state === 'ok' ? `current (${backups.age_hours}h ago)`
                                 : backups.state === 'stale' ? `STALE — ${backups.age_hours}h old`
                                 : backups.state === 'missing' ? 'NONE — no export has ever landed'
                                 : `unknown — ${backups.reason ?? 'could not be checked'}`
                               }
                               ok={backups.state === 'ok'} />
                          {backups.bucket && <Row label="bucket" value={backups.bucket} />}
                          {backups.object && <Row label="newest object" value={backups.object} />}
                          <Row label="rotates after" value="7 days (bucket lifecycle rule)" />
                        </div>
                        {(backups.state === 'stale' || backups.state === 'missing') && (
                          <div className="panel st-alarm">
                            The nightly database export is not landing. Cloud SQL's own
                            automated backups are unaffected and still run — but those live
                            inside the instance and die with it. This is the copy that
                            survives losing the instance or the project, so until it is
                            fixed, that recovery path does not exist.
                          </div>
                        )}
                        {backups.state === 'unknown' && (
                          <p className="muted">
                            Age could not be read here, which is not the same as “no backup”.
                            Check the bucket directly before concluding anything.
                          </p>
                        )}
                      </section>
                    )}
                    {/* Starting an export and pulling one back out are ADMINISTRATION, not
                        visibility — Priori staff read the card above, but only a platform
                        admin gets these. The routes enforce the same thing server-side and
                        answer 404 to everyone else; this only decides what is drawn. */}
                    {session.user.is_platform_admin && <BackupControls />}
            </>
          ),
        }] : []),
        {
          id: 'navigation',
          label: 'Navigation',
          body: () => (
            <>

                    <section className="st-section">
                      <h2 className="st-h2">Navigation <Source live /></h2>
                      <p className="muted rz-note">
                        Every destination in the rail, from the same function that draws it —
                        so this cannot drift from the thing it documents. <code>react</code>{' '}
                        means the page is ported; <code>flask</code> means it is still served
                        by the API through the frontend&rsquo;s proxy. The counts are the
                        migration&rsquo;s progress board.
                      </p>
                      <table className="rz-table">
                        <thead>
                          <tr><th style={{ width: '16rem' }}>Section</th><th>Label</th><th>URL</th><th style={{ width: '8rem' }}>Served by</th></tr>
                        </thead>
                        <tbody>
                          {nav.map((n) => (
                            <tr key={`${n.section}-${n.label}`}>
                              <td className="muted">{n.section}</td>
                              <td>{n.label}</td>
                              <td className="mono">{n.href}</td>
                              <td className={n.served === 'not built' ? 'dt-public' : ''}>{n.served}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </section>
            </>
          ),
        },
        ...(!build?.available || !doc ? [{
          id: 'build',
          label: 'Build document',
          body: () => (
            <section className="st-section">
              <h2 className="st-h2">Build document</h2>
              <div className="panel rz-absent">
                {build?.reason ?? 'This image published no build document.'}
              </div>
            </section>
          ),
        }] : [
          {
            id: 'build',
            label: 'Build',
            body: () => (
              <>
                      <section className="rz-section">
                        <h2 className="rz-h2">Commit</h2>
                        <table className="rz-kv">
                          <tbody>
                            <tr><th>SHA</th><td className="mono">{doc.commit.sha ?? '—'}</td></tr>
                            <tr><th>Subject</th><td>{doc.commit.subject ?? '—'}</td></tr>
                            <tr><th>Branch</th><td className="mono">{doc.commit.branch ?? '—'}</td></tr>
                            <tr><th>Committed</th><td className="mono">{doc.commit.committed_at ?? '—'}</td></tr>
                            <tr><th>Document written</th><td className="mono">{doc.generated_at ?? '—'}</td></tr>
                          </tbody>
                        </table>
                      </section>
                      <section className="rz-section">
                        <h2 className="rz-h2">Architecture</h2>
                        <Architecture layers={doc.architecture.layers} edges={doc.architecture.edges} />
                      </section>
                      <section className="rz-section">
                        <h2 className="rz-h2">Build checks</h2>
                        <p className="muted rz-note">
                          Every claim above and below carries a file and a phrase, checked
                          when the image was built. The build fails if one no longer
                          matches, so a published document&rsquo;s checks all pass — what is
                          worth reading is <em>what</em> was checked.
                        </p>
                        <div className="rz-checks">
                          {doc.checks.map((c) => (
                            <div className={`rz-check${c.ok ? '' : ' is-bad'}`} key={`${c.kind}-${c.id}`}>
                              <span className="rz-check__mark">{c.ok ? '✓' : '✗'}</span>
                              <span className="rz-check__kind t-label">{c.kind.replace(/_/g, ' ')}</span>
                              <span className="rz-check__title">{c.title}</span>
                              <span className="rz-check__file mono">{c.file}</span>
                            </div>
                          ))}
                        </div>
                      </section>
              </>
            ),
          },
          {
            id: 'api',
            label: 'API surface',
            body: () => (
              <>
                      <section className="rz-section">
                        <h2 className="rz-h2">API surface</h2>
                        <div className="rz-stats">
                          <div className="rz-stat">
                            <div className="t-metric">{build.live_api.census.total}</div>
                            <div className="t-label">routes, live</div>
                          </div>
                          <div className="rz-stat">
                            <div className="t-metric">{doc.api.census?.total ?? '—'}</div>
                            <div className="t-label">routes, recorded</div>
                          </div>
                          <div className={`rz-stat${build.live_api.census.public > 0 ? ' is-alarm' : ''}`}>
                            <div className="t-metric">{build.live_api.census.public}</div>
                            <div className="t-label">public — no guard</div>
                          </div>
                          <div className="rz-stat">
                            <div className="t-metric">{build.live_api.census.org_scoped}</div>
                            <div className="t-label">org-scoped</div>
                          </div>
                        </div>
                        {doc.api.census && doc.api.census.total !== build.live_api.census.total && (
                          <div className="panel rz-absent rz-note">
                            The running process routes {build.live_api.census.total} routes; the
                            image recorded {doc.api.census.total}. They should match — this is
                            either an unbuilt local change or an image that does not describe
                            itself.
                          </div>
                        )}
                        <h3 className="rz-h3">Unguarded routes</h3>
                        <p className="muted rz-note">
                          Reachable without authentication. Most are correct — sign-in,
                          legal, health. The list is here because its <em>length</em> moves
                          without anyone deciding it should.
                        </p>
                        <table className="rz-table">
                          <thead>
                            <tr><th style={{ width: '7rem' }}>Methods</th><th>Rule</th><th>Module</th></tr>
                          </thead>
                          <tbody>
                            {build.live_api.public_routes.map((r) => (
                              <tr key={r.rule}>
                                <td className="t-data">{r.methods.join(', ')}</td>
                                <td className="mono">{r.rule}</td>
                                <td className="muted mono">{r.module}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </section>
              </>
            ),
          },
          {
            id: 'security',
            label: 'Security',
            body: () => (
              <>
                      <section className="rz-section">
                        <h2 className="rz-h2">Security</h2>
                        {doc.security.controls.map((c) => (
                          <div className="rz-control" key={c.id}>
                            <div className="rz-control__title">{c.title}</div>
                            <div className="muted">{c.detail}</div>
                            <div className="mono rz-control__file">{c.verify[0]}</div>
                          </div>
                        ))}
                        <h3 className="rz-h3">Accepted risks</h3>
                        {doc.security.accepted_risks.map((r) => (
                          <div className="rz-risk" key={r.id}>
                            <div className="rz-control__title">{r.title}</div>
                            <div className="muted">{r.impact}</div>
                            <div className="mono rz-control__file">ledgered in {r.ledgered}</div>
                          </div>
                        ))}
                        <h3 className="rz-h3">What the deploy smoke test asserts</h3>
                        <ul className="rz-list muted">
                          {doc.security.smoke_assertions.map((a) => <li key={a}>{a}</li>)}
                        </ul>
                      </section>
              </>
            ),
          },
          {
            id: 'tests',
            label: 'Tests',
            body: () => (
              <>
                <section className="rz-section">
                  <h2 className="rz-h2">Tests <Source when={doc.generated_at} /></h2>
                  {!doc.tests.available ? (
                    <div className="panel rz-absent">{doc.tests.reason}</div>
                  ) : (
                    <>
                      <Provenance of={doc.tests.provenance} />
                      {/* Per suite, not summed. They run in different places
                          against different risks, and one total hides which
                          half is thin — the only thing the number is for. */}
                      {doc.tests.suites?.map((s) => (
                        <div key={s.suite} className="rz-suite">
                          <h3 className="rz-h3">{s.suite}</h3>
                          <div className="rz-stats">
                            <Stat n={s.totals.tests} label="tests" />
                            <Stat n={s.totals.failures + s.totals.errors}
                                  label="failures" alarm={s.totals.failures + s.totals.errors > 0} />
                            <Stat n={s.totals.skipped} label="skipped" />
                            <Stat n={s.totals.seconds} label="seconds" />
                          </div>
                        </div>
                      ))}
                    </>
                  )}
                </section>

                <section className="rz-section">
                  <h2 className="rz-h2">Coverage <Source when={doc.generated_at} /></h2>
                  {!doc.coverage.available ? (
                    <div className="panel rz-absent">{doc.coverage.reason}</div>
                  ) : (
                    <>
                      <p className="muted rz-note">
                        Measured on the commit this image was built from. The
                        headline is the least useful number here — what matters
                        is the list underneath it, which is where a bug would go
                        unnoticed. Files are ranked by UNCOVERED statements, not
                        by percentage: forty uncovered lines in a route matter
                        more than four in a helper.
                      </p>
                      {doc.coverage.reports?.map((r) => (
                        <div key={r.suite} className="rz-suite">
                          <h3 className="rz-h3">{r.suite}</h3>
                          <div className="rz-stats">
                            <Stat n={r.percent ?? 0} label={`% ${r.measure}`}
                                  alarm={(r.percent ?? 0) < r.floor} />
                            <Stat n={r.files} label="files" />
                            <Stat n={r.below_floor} label={`below ${r.floor}%`}
                                  alarm={r.below_floor > 0} />
                          </div>
                          <table className="rz-table">
                            <thead>
                              <tr>
                                <th>File</th>
                                <th style={{ width: '8rem' }}>Uncovered</th>
                                <th style={{ width: '8rem' }}>Statements</th>
                                <th style={{ width: '7rem' }}>Covered</th>
                              </tr>
                            </thead>
                            <tbody>
                              {r.worst.map((f) => (
                                <tr key={f.path}>
                                  <td className="mono">{f.path}</td>
                                  <td className="t-data">{f.statements - f.covered}</td>
                                  <td className="muted">{f.statements}</td>
                                  <td className={f.percent < r.floor ? 'dt-public' : ''}>
                                    {f.percent}%
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      ))}
                    </>
                  )}
                </section>

                <section className="rz-section">
                  <h2 className="rz-h2">What no suite checks</h2>
                  <table className="rz-table">
                    <thead><tr><th>Check</th><th>Why not automated</th><th style={{ width: '10rem' }}>Who</th></tr></thead>
                    <tbody>
                      {doc.tests.manual_checks.map((m) => (
                        <tr key={m.check}>
                          <td>{m.check}</td><td className="muted">{m.why}</td><td className="muted">{m.who}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </section>
              </>
            ),
          },
          ...(doc.infrastructure ? [{
            id: 'infrastructure',
            label: 'Infrastructure',
            body: () => (
              <section className="rz-section">
                <h2 className="rz-h2">Infrastructure as code</h2>
                <p className="muted rz-note">{doc.infrastructure!.summary}</p>
                <table className="rz-table">
                  <thead><tr><th style={{ width: '18rem' }}>Stack</th><th>Owns</th></tr></thead>
                  <tbody>
                    {doc.infrastructure!.stack.map((e) => (
                      <tr key={e.path}>
                        <td className="mono">{e.path}</td>
                        <td className="muted">{e.owns}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <h3 className="rz-h3">Deliberately not Terraform&rsquo;s</h3>
                <ul className="rz-list">
                  {doc.infrastructure!.not_owned.map((n) => <li key={n} className="muted">{n}</li>)}
                </ul>
                <p className="muted rz-note">
                  <strong>Applied by.</strong> {doc.infrastructure!.applied_by}
                </p>
                {/* Says which of the two runs produced this, rather than
                    implying the paths were checked when they could not be. */}
                <p className="muted rz-note">
                  {doc.infrastructure!.paths_verified
                    ? 'Every path above was confirmed to exist when this document was built.'
                    : 'Paths not checked in this build — infra/ is excluded from the image build context.'}
                </p>
              </section>
            ),
          }] : []),
          {
            id: 'promotion',
            label: 'Promotion',
            body: () => (
              <>
                      <section className="rz-section">
                        <h2 className="rz-h2">Promotion</h2>
                        <p className="muted rz-note">{doc.promotion.summary}</p>
                        <table className="rz-table">
                          <thead><tr><th style={{ width: '7rem' }}>Environment</th><th>Host</th><th>How</th></tr></thead>
                          <tbody>
                            {doc.promotion.stages.map((s) => (
                              <tr key={s.env}>
                                <td className="t-data">{s.env}</td>
                                <td className="mono">{s.host}</td>
                                <td className="muted">{s.how}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                        <p className="muted rz-note">
                          <strong>Rollback.</strong> {doc.promotion.rollback.how} — {doc.promotion.rollback.caveat}
                        </p>
                      </section>
              </>
            ),
          },
        ]),
      ]} />

    </>
  )
}
